import { useEffect, useRef, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import VoiceMicButton from '@/components/VoiceMicButton';
import { cn } from '@/lib/cn';
import type { Nudge } from './api';
import { ConsultationComposer } from './ConsultationComposer';
import { ConsultationTranscript, type ChatMessage } from './ConsultationTranscript';
import type { SectionChange } from './documentSections';
import { NudgeList } from './NudgeList';
import { PersonalisedChip } from './PersonalisedChip';
import { ProposalCard } from './ProposalCard';
import { SuggestionChips } from './SuggestionChips';
import type { AppliedMemoryItem, GenerationSuggestion } from './streamGeneration';
import type { DoctorPreference } from './memoryApi';

/**
 * v3 conversational layout — the right-hand "assistant" column
 * (docs/design/consultation/Conversation.dc.html): never collapses, so the
 * screen always reads as a conversation in progress rather than a form.
 * Rendered twice by ConsultationChat.tsx, sharing all of this component's
 * props — once as the static `xl:` (≥1280px) column, once as the
 * narrow-screen `dock` variant (`LiveStates.dc.html`'s "narrow screen"
 * board) — so the breakpoint switch is pure CSS (Tailwind's `xl`), not a
 * JS media query, and both presentations always agree with each other.
 *
 * Four states, in priority order — see this file's own plan for why:
 *   1. AI off  → the completeness checklist + a plain "add to the note"
 *      dictation control, no chat composer that would only 403.
 *   2. Approved → read-only transcript history, no live composer (there is
 *      no "reopen an approved note" flow today — see the header comment on
 *      the footer branch below for why that's stated rather than offered).
 *   3. Empty conversation → chips only, never a blank panel.
 *   4. Normal → transcript, suggestions, the pinned composer.
 */
export interface AssistantColumnProps {
  variant: 'column' | 'dock';
  /** Header label for the `column` variant and the collapsed dock's fallback
   * text — defaults to "Consultation assistant" so every existing caller is
   * unaffected; DischargeSummaryChat.tsx passes "Discharge assistant". */
  title?: string;
  aiEnabled: boolean | null;
  approved: boolean;
  msgs: ChatMessage[];
  /** See ConsultationTranscript.tsx's own doc comment — DischargeSummaryChat.tsx
   * passes its own detector so an unmarked first-turn reply doesn't leak into
   * the transcript; every other caller leaves this unset. */
  isDocumentText?: (text: string) => boolean;
  msgsLoading: boolean;
  loading: boolean;
  streamingText: string | null;
  statusLabel: string;
  changesByIndex: Map<number, SectionChange[]>;
  latestChangeIndex: number | null;
  onJumpToSection: (key: string) => void;
  onUndoTurn: () => void;
  suggestions: GenerationSuggestion[];
  onSendSuggestion: (message: string) => void;
  onRemember: (suggestion: Extract<GenerationSuggestion, { type: 'remember' }>) => void;
  appliedMemory: AppliedMemoryItem[];
  onOpenPreferences: () => void;
  proposal: DoctorPreference | null;
  onProposalDone: () => void;
  onFillFromClinicalData: () => void;
  nudges: Nudge[];
  onDictateIntoNote: (text: string) => void;
  inp: string;
  onInpChange: (value: string) => void;
  onSend: () => void;
  onDictateIntoComposer: (text: string) => void;
  canRecord: boolean;
  recording: boolean;
  onToggleRecording: () => void;
  latestLine: string | null;
  dockExpanded: boolean;
  onToggleDock: () => void;
  /** Aborts the in-flight request — always offered from the first moment of
   * a request (docs/design/consultation/LiveStates.dc.html's "Slow
   * provider"/"never a spinner with no exit"), not gated to any delay. */
  onStop: () => void;
  className?: string;
  /** Replaces the default empty-conversation chips (Describe examination /
   * Fill from clinical data) — the ED document assistant offers "which
   * document?" starters instead. */
  emptyState?: ReactNode;
  /** Composer placeholder; defaults to the consultation wording. */
  composerPlaceholder?: string;
  /** Replaces the AI-off completeness checklist body. */
  aiOffBody?: ReactNode;
}

export function AssistantColumn({
  variant, title = 'Consultation assistant', aiEnabled, approved, msgs, isDocumentText, msgsLoading, loading, streamingText, statusLabel,
  changesByIndex, latestChangeIndex, onJumpToSection, onUndoTurn,
  suggestions, onSendSuggestion, onRemember, appliedMemory, onOpenPreferences, proposal, onProposalDone,
  onFillFromClinicalData, nudges, onDictateIntoNote,
  inp, onInpChange, onSend, onDictateIntoComposer, canRecord, recording, onToggleRecording,
  latestLine, dockExpanded, onToggleDock, onStop, className,
  emptyState, composerPlaceholder = 'Describe findings, diagnosis and treatment…', aiOffBody,
}: AssistantColumnProps) {
  const empty = aiEnabled !== false && !approved && msgs.length === 0 && !loading && !msgsLoading;

  // Keeps the transcript scrolled to its latest turn as new messages/deltas
  // arrive — encapsulated here (rather than a ref threaded down from
  // ConsultationChat.tsx) since both the column and dock renders of this
  // same component need it independently.
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = transcriptScrollRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [msgs, streamingText]);

  let body: ReactNode;
  let footer: ReactNode;

  if (aiEnabled === false) {
    body = (
      <div className="flex flex-1 min-h-0 flex-col gap-2.5 overflow-y-auto p-2.5">
        {aiOffBody ?? (nudges.length > 0
          ? <NudgeList nudges={nudges} />
          : <div className="text-[12px] text-ink-muted">Nothing flagged — the note looks complete.</div>)}
      </div>
    );
    footer = (
      <div className="flex shrink-0 items-center gap-2 border-t border-line px-2.5 py-2.5">
        <VoiceMicButton onText={onDictateIntoNote} title="Dictate — added to the note" />
        <span className="text-[11.5px] text-ink-muted">Dictate — added straight to the note</span>
      </div>
    );
  } else if (approved) {
    body = (
      <div ref={transcriptScrollRef} className="flex flex-1 min-h-0 flex-col gap-2.5 overflow-y-auto p-2.5">
        <ConsultationTranscript
          msgs={msgs} msgsLoading={false} loading={false} streamingText={null}
          changesByIndex={new Map()} latestChangeIndex={null} onJump={onJumpToSection} onUndoTurn={onUndoTurn}
          isDocumentText={isDocumentText}
        />
      </div>
    );
    // No "reopen to amend" action here on purpose — an approved consultation
    // has no re-edit/re-save flow in this codebase today (each approval is a
    // new consultations row; amending one is a real product decision, not a
    // layout one). Stated plainly instead of offering a control that would
    // do nothing.
    footer = (
      <div className="shrink-0 border-t border-line px-2.5 py-2.5 text-[11.5px] text-ink-muted">
        ✅ Approved — this consultation note is final.
      </div>
    );
  } else if (empty) {
    body = (
      <div className="flex flex-1 min-h-0 flex-wrap content-center gap-1.5 overflow-y-auto p-2.5">
        {emptyState ?? (<>
        <Button type="button" size="sm" variant="ghost" onClick={() => onInpChange('Describe the examination findings: ')}>
          Describe examination
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={loading} onClick={onFillFromClinicalData}>
          <Sparkles className="h-3.5 w-3.5 text-teal" aria-hidden="true" /> Fill from clinical data
        </Button>
        {canRecord && (
          <Button type="button" size="sm" variant="ghost" onClick={onToggleRecording}>
            🎙 Record conversation
          </Button>
        )}
        </>)}
      </div>
    );
    footer = (
      <ConsultationComposer
        value={inp} onChange={onInpChange} onSend={onSend} onDictate={onDictateIntoComposer}
        loading={loading} placeholder={composerPlaceholder} canRecord={canRecord} recording={recording}
        onToggleRecording={onToggleRecording}
      />
    );
  } else {
    body = (
      <>
        <div ref={transcriptScrollRef} className="flex flex-1 min-h-0 flex-col gap-2.5 overflow-y-auto p-2.5">
          <ConsultationTranscript
            msgs={msgs} msgsLoading={msgsLoading} loading={loading} streamingText={streamingText}
            changesByIndex={changesByIndex} latestChangeIndex={latestChangeIndex} onJump={onJumpToSection}
            onUndoTurn={onUndoTurn} isDocumentText={isDocumentText}
          />
        </div>
        {/* "status line: idle / thinking / writing §" — its own line between
            the transcript and the suggestion chips, matching the wireframe's
            position, rather than folded into the header (marker "status
            line" in Conversation.dc.html; Stop lives here too, matching
            LiveStates.dc.html board A). Idle shows nothing, deliberately —
            a permanent "● idle" line the wireframe doesn't actually show
            added visual noise for no information. */}
        {loading && (
          <div className="flex shrink-0 items-center gap-1.5 border-t border-line px-2.5 py-1.5 text-[11px] font-semibold text-teal">
            <span className="h-1.5 w-1.5 rounded-full bg-teal" aria-hidden="true" />
            {statusLabel}
            <button type="button" onClick={onStop} className="ml-auto rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-teal hover:text-teal">
              Stop
            </button>
          </div>
        )}
        {!loading && (suggestions.length > 0 || appliedMemory.length > 0) && (
          <div className="flex shrink-0 flex-col gap-1.5 border-t border-line px-2.5 py-2">
            <SuggestionChips suggestions={suggestions} onSend={onSendSuggestion} onRemember={onRemember} />
            <Button type="button" size="sm" variant="ghost" className="self-start" disabled={loading} onClick={onFillFromClinicalData}>
              <Sparkles className="h-3.5 w-3.5 text-teal" aria-hidden="true" /> Fill from clinical data
            </Button>
            {appliedMemory.length > 0 && <PersonalisedChip applied={appliedMemory} onOpenPreferences={onOpenPreferences} />}
          </div>
        )}
        {!loading && proposal && (
          <div className="shrink-0 border-t border-line px-2.5 py-2">
            <ProposalCard proposal={proposal} onDone={onProposalDone} />
          </div>
        )}
      </>
    );
    footer = (
      <ConsultationComposer
        value={inp} onChange={onInpChange} onSend={onSend} onDictate={onDictateIntoComposer}
        loading={loading} placeholder={composerPlaceholder} canRecord={canRecord} recording={recording}
        onToggleRecording={onToggleRecording}
      />
    );
  }

  if (variant === 'dock') {
    const collapsedLabel = statusLabel || latestLine || title;
    return (
      // sticky, not fixed, so it stacks above ConsultStage's own sticky
      // StageActionBar (the commit bar) instead of overlapping it — both are
      // page-level sticky-bottom elements, so this needs its own bottom
      // offset clear of that bar's height. bottom-14 (56px) matches that
      // bar's typical single-row height; re-check visually if the bar's own
      // height ever changes.
      <div className={cn('sticky bottom-14 z-10 flex shrink-0 flex-col border-t-2 border-line bg-card xl:hidden', className)}>
        {dockExpanded && <div className="flex max-h-[55vh] flex-col overflow-hidden border-b border-line">{body}</div>}
        <div className="flex items-center gap-2 px-2.5 py-2">
          <button
            type="button"
            onClick={onToggleDock}
            className="flex min-w-0 flex-1 items-center gap-2 text-left text-[11.5px] text-ink-muted hover:text-ink"
          >
            {dockExpanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            <span className="min-w-0 flex-1 truncate">{collapsedLabel}</span>
          </button>
          {loading && (
            <button type="button" onClick={onStop} className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-teal hover:text-teal">
              Stop
            </button>
          )}
        </div>
        {footer}
      </div>
    );
  }

  // Width and xl-only visibility are the parent's job now (ConsultationChat.tsx
  // wraps this in a div it resizes by drag), not this component's own —
  // it just fills whatever box it's given.
  return (
    <div className={cn('flex h-full w-full flex-col rounded-[var(--radius)] border border-line bg-card', className)}>
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-2.5 py-2 text-[12px]">
        <b className="text-ink">{title}</b>
        <span className="text-[11px] text-ink-muted">{msgs.length} turn{msgs.length === 1 ? '' : 's'}</span>
      </div>
      {body}
      {footer}
    </div>
  );
}
